<script setup>
import { useAuthStore } from '@/stores/authStore.js'
import AuthedLayout from '@/layouts/AuthedLayout.vue'
import { computed, onMounted, ref } from 'vue'
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { Format } from '@/models/utils/FormatUtils.js'
import EndpointTableCellMenu from '@/components/Tables/EndpointTableCellMenu.vue'
import AddDocumentModal from '@/views/Documents/Upload/AddDocumentModal.vue'
import { documentsApi } from '@/api/Documents/DocumentsApi.js'
import SubmitDocumentForm from '@/views/Documents/Upload/SubmitDocumentForm.vue'
import PublishDocumentForm from '@/views/Documents/Upload/PublishDocumentForm.vue'
import DeleteDocumentForm from '@/views/Documents/Upload/DeleteDocumentForm.vue'
import DocumentHistoryList from '@/views/Documents/Upload/DocumentHistoryList.vue'
import { Banner, Toaster } from '@/models/utils/Toaster.js'
import { Tenants } from '@/models/tenants/Tenants.js'
import CheckBox from '@/components/Forms/CheckBox.vue'
import PortalDataTable from '@/components/DataTable/PortalDataTable.vue'
import { DataTableEndpointDefinition } from '@/models/datatable/DataTableEndpointDefinition.js'
import { UserpilotService } from '@/models/utils/UserpilotUtils.js'

const authStore = useAuthStore();

const toaster = new Toaster()
const banner = new Banner()

const showAddModal = ref(false);
const tagData = ref([])
const modalError = ref({ 'error_eh': false, 'error_msg': 'Error loading Tags. Please try reloading the page.', 'error': null })
const recordData = ref(null)
const showSubmitModal = ref(false)
const showPublishModal = ref(false)
const showDeleteModal = ref(false)
const showHistory = ref(false)
const archiveAfterDays = ref(0)
const includeArchived = ref(false)

onMounted(async () => {
	loadTags()

	archiveAfterDays.value = Number(JSON.parse(authStore.getAuth.tenantInfo().settings_json)?.documents_archive_after_days?.value)
})

function toggleArchivedContent() {
	table.value.loadData()
}

function loadTags()
{
	documentsApi.loadTags(
		false,
		(response) => {
			tagData.value = response
		},
		(error) => {
			modalError.value = { 'error_eh': true, 'error_msg': 'Error loading Tags. Please try reloading the page.', 'error': error }
		}
	)
}

const table = ref(null)
const columns = [
	{ field: "name", title: "Name", sort: true, search: true, width: '30%' },
	{ field: "tags", title: "Tags", sort: false, width: '30%' },
	{ field: "creator_name", title: "Created By", sort: false },
	{ field: "updated_at", title: "Date", sort: true, width: '1%' },
	{ field: "latest_status", title: "Status", sort: true, width: '1%' },
	{ field: "actions", title: "Actions", sort: false, width: '1%' }
]

const advancedSearchParams = computed(() => {
	return [
		{
			"id": 'archive',
			"value": includeArchived.value
		}
	]
})

const endpoints = [
	new DataTableEndpointDefinition({
		endpointRoute: 'documents/all',
		endpointParams: { query_config: { "organization_id": authStore.activeOrganizationId, "include_access_logs": false, "include_authorized_users": true, "include_status_changes": true, "include_tags": true } },
		advancedSearchParams: advancedSearchParams,
		initialSortColumn: 'updated_at',
		initialSortDirection: 'desc',
		searchPlaceholder: 'Search by name',
		columns: columns,
	})
]

function canDelete(p_current_status, p_record)
{
	//Update service if you change this
	const user_id = authStore.getAuth?.user()?.id ? authStore.getAuth?.user().id : 0;
	if (p_current_status === 'Draft' || p_current_status === 'Rejected')
	{
		return user_id === p_record.submitter_id || authStore.isPortalAdmin || authStore.isOrganizationAdmin
	}
	return authStore.canApproveDocuments || authStore.isPortalAdmin || authStore.isOrganizationAdmin
}

function canPublish(p_current_status, p_record)
{
	const user_id = authStore.getAuth?.user()?.id ? authStore.getAuth?.user().id : 0;
	if (p_current_status !== 'Submitted') return false;
	return authStore.canApproveDocuments && user_id !== p_record.submitter_id
}

function canDownload(p_record)
{
	const isUploaded = !['error', 'initiated', 'queued', 's3_in_transit'].includes(p_record.status)

	const user_id = authStore.getAuth?.user()?.id ? authStore.getAuth?.user().id : 0;

	return isUploaded && (user_id === p_record.submitter_id || authStore.canDownloadDocuments)
}

function getMenuItems(p_current_status, p_record)
{
	const status_changes = p_record.status_changes

	let items = [];
	let i = 1;

	const user_id = authStore.getAuth?.user()?.id ? authStore.getAuth?.user().id : 0;

	const can_delete = canDelete(p_current_status, p_record)
	const can_submit = user_id === p_record.submitter_id
	const can_publish = canPublish(p_current_status, p_record)
	const can_download = canDownload(p_record)

	//console.log('getMenuItems', p_current_status, p_record)
	switch (p_current_status)
	{
		case 'Draft':
			//items.push({ id: i++, text:'Edit' })
			items.push({ id: i++, text:'Submit', hidden: !can_submit })
			items.push({ id: i++, text:'History', hidden: status_changes.length === 1 })
			items.push({ id: i++, text:'Delete', hidden: !can_delete })
			items.push({ id: i++, text:'Download', hidden: !can_download })
			break;

		case 'Submitted':
			//items.push({ id: i++, text:'Reject' })
			items.push({ id: i++, text:'Publish', hidden: !can_publish })
			items.push({ id: i++, text:'History', hidden: status_changes.length === 1 })
			items.push({ id: i++, text:'Delete', hidden: !can_delete })
			items.push({ id: i++, text:'Download', hidden: !can_download })
			break;

		case 'Deleted':
			items.push({ id: i++, text:'History', hidden: status_changes.length === 1 })
			items.push({ id: i++, text:'Download', hidden: !can_download })
			break;

		case 'Published':
			items.push({ id: i++, text:'History', hidden: status_changes.length === 1 })
			items.push({ id: i++, text:'Delete', hidden: !can_delete })
			items.push({ id: i++, text:'Download', hidden: !can_download })
			break;

		case 'Rejected':
			items.push({ id: i++, text:'Submit', hidden: !can_submit })
			items.push({ id: i++, text:'History', hidden: status_changes.length === 1 })
			items.push({ id: i++, text:'Delete', hidden: !can_delete })
			items.push({ id: i++, text:'Download', hidden: !can_download })
			break;

		case 'Failed':
			items.push({ id: i++, text:'Delete', hidden: !can_delete })
			break;
	}

	return items;
}

function toggleModal(p_type, p_show_eh)
{
	switch(p_type)
	{
		case "add":
			showAddModal.value = p_show_eh
			break;
		case "submit":
			showSubmitModal.value = p_show_eh
			break;
		case "publish":
			showPublishModal.value = p_show_eh
			break;
		case "delete":
			showDeleteModal.value = p_show_eh
			break;
		case "history":
			showHistory.value = p_show_eh
			break;
	}
}

function onCloseModal(p_type, p_reload_eh)
{
	toggleModal(p_type, false)

	if (p_reload_eh) {
		reloadTable()
	}
}

function reloadTable()
{
	if (table.value)
	{
		table.value.loadData()
	}
	else
	{
		location.reload()
	}
}

function onMenuItemSelected(p_menu_item, p_record)
{
	switch (p_menu_item.text) {
		case "Submit":
			recordData.value = p_record
			toggleModal('submit', true)
			break;
		case "Edit":
			//Do edity things
			break;
		case "Delete":
			//Do Delete
			recordData.value = p_record
			toggleModal('delete', true)
			break;
		case "Publish":
			recordData.value = p_record
			toggleModal('publish', true)
			break;
		case "History":
			recordData.value = p_record
			toggleModal('history', true)
			break;
		case "Download":
			downloadDocument(p_record)
			break;
		default:
			alert("Not yet implemented")
			break;
	}
}

function getStatusStyle(p_status)
{
	let style = 'bg-neutral-light text-label';
	switch (p_status)
	{
		case 'Draft':
			style = 'bg-neutral-light text-label'
			break;

		case 'Approved':
		case 'Submitted':
			style = 'bg-information text-information-on-color';
			break;

		case 'Published':
			style = 'bg-success text-success-on-color';
			break;

		case 'Rejected':
			style = 'bg-lo-warning-subtle text-lo-warning-on-color';
			break;

		case 'Deleted':
			style = 'bg-error-light text-error-on-color';
			break;

		case 'Failed':
			style = 'bg-error-light text-error-on-color';
			break;
	}
	return style;
}

function downloadDocument(p_record) {
	toaster.info('Requesting download...', { timeout: false })

	documentsApi.download(
		'manage',
		[ p_record ],
		authStore.getAuth.tenantInfo().name,
		authStore.getActiveOrganization.organization_name,
		() => {
			toaster.clear()
			toaster.success('Document downloaded started...')

			UserpilotService.track('documents_download', {
				"user_id": authStore.getAuth.user().id,
				"organization_id": authStore.activeOrganizationId
			});
		},
		(p_err) => {
			handleDownloadError(p_err)
		}
	)
}

async function handleDownloadError(p_err)
{
	try
	{
		const devEnvEh = Tenants.isDevHost() && authStore.isPortalAdmin;
		const text = await p_err.response.data.text();
		const data = JSON.parse(text);

		toaster.clear()
		if (data['errors'] && data['errors'].length > 0)
		{
			const err_msgs = data['errors'].map(p_item => p_item.detail);
			if (devEnvEh)
			{
				banner.error("Dev Only Banner:<br>" + err_msgs.join('<br/>'));
			}

			const err = data['errors'].find((p_item) => p_item.status === 403);
			if (err)
			{
				toaster.error('You do not have permission to download documents.');
			}
			else
			{
				toaster.error('Document failed to download.');
			}

		}
		else
		{
			toaster.error('Document failed to download.')
		}
	}
	catch(e)
	{
		console.error(e)
	}
}

function getStatusText(record) {
	if (record.status === 'error') {
		return 'Failed'
	}
	else return record.latest_status
}
</script>

<template>
	<AuthedLayout>
		<p class="dashboard-title">Documents</p>

		<div class="p-8 rounded-xl bg-neutral border border-neutral-subtle card-shadow">
			<div class="flex flex-row mb-4">
				<p class="admin-center-subtitle">Manage</p>
				<SubmitButton v-if="authStore.canUploadDocuments" label="Upload" @click="toggleModal('add', true)" size="small"/>
			</div>

			<PortalDataTable
				ref="table"
				:endpoints="endpoints"
			>
				<template #advanced-search>
					<div class="flex flex-row mr-1 mt-1 justify-end">
						<div class="flex w-full justify-end overflow-hidden">
							<Popper :content="`Documents are considered to be archived after ${archiveAfterDays} days.`" hover arrow placement="bottom-start">
								<CheckBox input-id="cbx_archive" label="Include archived content" label-location="left" class="my-2" :checked="false" v-model="includeArchived" @checked-changed="toggleArchivedContent" />
							</Popper>
						</div>
					</div>
				</template>

				<template #name="data">
					<div class="flex space-x-1">
						<Popper v-if="['initiated', 'queued', 's3_in_transit'].includes(data.value.status)" content="This document is still in the process of uploading and will be available soon." hover arrow placement="bottom-end">
							<img class="size-5 min-w-5 min-h-5" src="@/assets/images/icon/file-upload.svg"/>
						</Popper>

						<Popper v-if="data.value.status === 'error'" content="This document has failed to upload and will need to be deleted and uploaded again." hover arrow placement="bottom-end">
							<img class="size-5 min-w-5 min-h-5" src="@/assets/images/icon/file-exclamation.svg"/>
						</Popper>

						<span class="userpilot-mask">{{ data.value.name }}</span>
					</div>
				</template>

				<template #tags="data">
					<div class="flex flex-wrap pt-2">
						<span v-for="(tag) in data.value.tags" :key="tag.id" class="inline text-accent-on-color bg-accent py-1 px-2 rounded mb-2 mr-2">
							{{ tag.name}}
						</span>
					</div>
				</template>

				<template #creator_name="data">
					<div class="userpilot-mask whitespace-nowrap">
						{{ data.value.creator_name }}
					</div>
				</template>

				<template #updated_at="data">
					<div class="whitespace-nowrap">
						{{ Format.date(data.value.updated_at) }}
					</div>
				</template>

				<template #latest_status="data">
					<div class="flex w-full justify-center whitespace-nowrap">
						<div class="flex justify-center items-center w-22 py-1 px-2 rounded-md"
								 :class="getStatusStyle(getStatusText(data.value))">
							{{ getStatusText(data.value) }}
						</div>
					</div>
				</template>

				<template #actions="data">
					<div class="flex w-full justify-center">
						<EndpointTableCellMenu
							:items="getMenuItems(getStatusText(data.value), data.value)"
							:record="data.value"
							:disabled="false"
							@onMenuItemSelected="onMenuItemSelected"
						/>
					</div>
				</template>
			</PortalDataTable>
		</div>
	</AuthedLayout>

	<Teleport to="html">
		<AddDocumentModal v-if="showAddModal" :tag-data="tagData" :modal-error="modalError" @evCloseModal="onCloseModal" />
	</Teleport>

	<Teleport to="html">
		<SubmitDocumentForm :show="showSubmitModal" :modal-data="recordData" :tag-data="tagData" @evCloseModal="onCloseModal"></SubmitDocumentForm>
	</Teleport>

	<Teleport to="html">
		<PublishDocumentForm :show="showPublishModal" :modal-data="recordData" :tag-data="tagData" @evCloseModal="onCloseModal"></PublishDocumentForm>
	</Teleport>

	<Teleport to="html">
		<DeleteDocumentForm :show="showDeleteModal" :modal-data="recordData" :tag-data="tagData" @evCloseModal="onCloseModal"></DeleteDocumentForm>
	</Teleport>

	<Teleport to="html">
		<DocumentHistoryList :show="showHistory" :modal-data="recordData" @evCloseModal="onCloseModal"/>
	</Teleport>
</template>

<style scoped>

</style>